import { FC, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, TrendingUp } from 'lucide-react';
import { IndiceDto } from 'api/indice';
import { Sizes } from 'enums/sizes';
import { indicesAPI } from 'services/swagger-api';
import { useDialog } from 'hooks/useDialog';
import { useActionMenu } from 'components/navigation/Dropdown/GenericDropdownButton';
import { Dropdown } from 'components/forms/Dropdown';
import { InputText } from 'components/forms/InputText';
import { CheckboxComponent } from 'components/forms/Checkbox';
import { BlockCard, StripedList } from 'back-office/shared/components/BlockCard';
import { useAsyncResource } from 'back-office/shared/hooks/useAsyncResource';
import { useBranches } from 'back-office/features/branches-view/useBranches';
import { IndiceModal } from './forms/IndiceModal';
import { formatIndiceValue, toValuePeriods } from './indiceValues';

/** Borne imposée par le back sur la recherche paginée. */
const MAX_PAGE_SIZE = 200;
const ALL_BRANCHES = 'all';

/**
 * Liste des indices du référentiel.
 *
 * Chaque ligne affiche la valeur en vigueur et sa date d'entrée en application : c'est
 * la seule information utile d'un coup d'œil, l'historique complet vivant dans le détail.
 */
export const IndicesList: FC = () => {
  const navigate = useNavigate();
  const branches = useBranches();
  const { openBlankDialog } = useDialog();
  const { createMenuItem } = useActionMenu();

  const [search, setSearch] = useState('');
  const [branchId, setBranchId] = useState<string>(ALL_BRANCHES);
  const [unassignedOnly, setUnassignedOnly] = useState(false);

  const fetchIndices = useCallback(
    () =>
      indicesAPI.searchIndices({
        searchIndicesCommand: {
          branchId: branchId === ALL_BRANCHES ? undefined : branchId,
          searchString: search.trim() || undefined,
          unassignedOnly,
          pageNumber: 1,
          pageSize: MAX_PAGE_SIZE,
        },
      }),
    [branchId, search, unassignedOnly]
  );
  const { data, isLoading, error, reload } = useAsyncResource(fetchIndices);

  const openIndice = (indice?: IndiceDto) =>
    openBlankDialog(
      { size: Sizes['3xl'] },
      <IndiceModal indice={indice} branches={branches} onSuccess={reload} />
    );

  const branchItems = useMemo(
    () => [
      { key: ALL_BRANCHES, label: 'Toutes les branches' },
      ...branches.map((branch) => ({ key: branch.id, label: branch.label })),
    ],
    [branches]
  );

  const indices = data?.items ?? [];

  return (
    <div className="flex flex-col gap-5">
      <BlockCard
        title="Indices"
        description="Valeurs de référence datées, rattachées à une ou plusieurs branches"
        icon={<TrendingUp className="h-4 w-4" />}
        headerExtra={
          <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-semibold text-white">
            {data?.totalCount ?? indices.length}
          </span>
        }
        actions={[
          createMenuItem('add', 'Créer un indice', () => openIndice(), { icon: 'add' }),
        ]}
        isLoading={isLoading}
        error={error}
        flushBody
      >
        <div className="flex flex-wrap items-end gap-3 border-b border-gray-100 px-5 py-3">
          <div className="min-w-[200px] flex-1">
            <InputText
              name="search"
              label="Rechercher"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Code ou libellé"
            />
          </div>
          <div className="min-w-[200px]">
            <Dropdown
              name="branchId"
              label="Branche"
              items={branchItems}
              selectedItem={branchItems.find((item) => item.key === branchId) ?? null}
              onSelect={(item) => setBranchId(item.key as string)}
              disabled={unassignedOnly}
            />
          </div>
          <div className="pb-3">
            <CheckboxComponent
              name="unassignedOnly"
              label="Sans branche uniquement"
              checked={unassignedOnly}
              onChange={setUnassignedOnly}
            />
          </div>
        </div>

        {indices.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-400">
            Aucun indice ne correspond à ces critères
          </p>
        ) : (
          <StripedList>
            {indices.map((indice) => {
              const periods = toValuePeriods(indice.values);
              const current = periods[0];

              return (
                <li key={indice.id}>
                  <button
                    type="button"
                    onClick={() => navigate(indice.id)}
                    className="flex w-full items-center gap-4 px-5 py-3 text-left"
                  >
                    <span className="w-24 flex-shrink-0 truncate font-mono text-[11px] text-gray-400">
                      {indice.code || '—'}
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-zinc-800">
                        {indice.label || '—'}
                      </span>
                      <span className="block truncate text-xs text-gray-400">
                        {indice.branches?.length
                          ? indice.branches.map((branch) => branch.branchCode).join(', ')
                          : 'Aucune branche'}
                      </span>
                    </span>

                    {current ? (
                      <span className="flex-shrink-0 text-right">
                        <span className="block text-sm font-bold tabular-nums text-primary">
                          {formatIndiceValue(current.value.value)}
                        </span>
                        <span className="block text-[11px] text-gray-400">
                          depuis le {current.startLabel}
                        </span>
                      </span>
                    ) : (
                      <span className="flex-shrink-0 rounded bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                        aucune valeur
                      </span>
                    )}

                    <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-300" />
                  </button>
                </li>
              );
            })}
          </StripedList>
        )}
      </BlockCard>
    </div>
  );
};

export default IndicesList;
